/* global runtime, mergeState, safeArray, num, getReportsModel, buildLedgerLines, equityReconRound, equityReconMonthRange, equityReconIsEquityCode, equityReconAccountRows, equityReconStatement, equityReconTransactions, equityReconDecision, equityReconDisposition, equityReconCapital, equityReconLiquidity, equityReconItems, equityReconCounts, equityReconSources, equityReconChecks */
// EGENKAPITAL — lesemodell for byråets egenkapitalskjerm
//
// Egenkapitalen er det ene området der «debet er lik kredit» er den minst
// interessante kontrollen. Et utbytte kan være riktig bokført på riktig konto
// til riktig tid og likevel være ulovlig utdelt, fordi generalforsamlingen
// aldri vedtok det. Skjermen skiller derfor de to: regnestykket
// (egenkapitaloppstillingen) og beslutningen (protokoll og vedtak).
//
// Modellen er READ-ONLY og regner ikke ut noe fagtall selv:
//   saldoer og bevegelser  → saldobalansen (`getReportsModel`)
//   posteringene bak       → `buildLedgerLines` (samme utvalg som SAF-T)
//   fjorårets resultat     → `getAnnualAccountsModel` (samme som årsregnskapet)
//   vedtak og eiere        → `annualMeetingsByYear` og `shareholders`
//
// LÅST VALG: området avstemmes HITTIL I ÅR, ikke for måneden alene.
// Egenkapitalen beveger seg noen få ganger i året — utbyttet vedtas i april, og
// resultatdisponeringen bokføres når årsoppgjøret er ferdig. Med et
// månedsvindu ville oppstillingen vært tom i elleve av tolv måneder, og
// utbyttet som mangler et vedtak ville forsvunnet ut av avstemmingen måneden
// etter at det ble bokført. Inngående er derfor årets inngående balanse, og
// bevegelsen går fram til periodeslutt. Utgående saldo er den samme som
// avstemmingskontoens bokførte saldo.

const equityReconTrialMap = (reports) => {
  const trial = new Map();
  for (const group of safeArray(reports?.trial)) {
    for (const account of safeArray(group.accounts)) trial.set(String(account.nr || ''), account);
  }
  return trial;
};

// Egenkapitalen ved utgangen av forrige periode, med det akkumulerte resultatet
// på samme grunnlag som inneværende periode. Uten resultatet ville endringen
// vært sammenlikningen av to forskjellige størrelser.
const equityReconPreviousPeriod = ({ range, resultTransferred, openingEquity }) => {
  if (range.monthIndex <= 0 || typeof getReportsModel !== 'function') return openingEquity;
  const reports = getReportsModel({
    year: range.year, months: [0, range.monthIndex - 1], includeChart: false,
  });
  const trial = equityReconTrialMap(reports);
  let equity = 0;
  for (const [code, account] of trial) {
    if (equityReconIsEquityCode(code)) equity += -num(account.ut);
  }
  const result = num(reports?.result?.resultat);
  return equityReconRound(equity + (resultTransferred ? 0 : result));
};

const EQUITY_RECON_EMPTY = {
  accountRows: [], statement: [], transactions: [], items: [], counts: { flagged: 0 },
  sources: [], checks: [], ledgerLines: [],
  main: {
    bookedEquity: 0, openingEquity: 0, periodResult: 0, computedEquity: 0,
    statementEquity: 0, unexplained: 0, previousEquity: 0, change: 0, resultTransferred: false,
  },
};

const buildEquityReconciliationView = ({ periodId, accounts, documents, state: suppliedState } = {}) => {
  const range = equityReconMonthRange(periodId);
  if (!range || typeof getReportsModel !== 'function') {
    return { periodId, ...EQUITY_RECON_EMPTY, range: null };
  }
  const state = suppliedState || mergeState(runtime.appState);
  const reports = getReportsModel({ year: range.year, months: [0, range.monthIndex], includeChart: false });
  const trial = equityReconTrialMap(reports);
  const lines = typeof buildLedgerLines === 'function'
    ? buildLedgerLines({ from: range.yearFrom, to: range.to, state })
    : [];

  const accountRows = equityReconAccountRows({ trial, areaAccounts: accounts, lines });
  const equityCodes = new Set(accountRows.map((row) => row.code));
  const decision = equityReconDecision({ state, year: range.year, documents });
  const transactions = equityReconTransactions({ lines, equityCodes, decision });
  const statement = equityReconStatement(accountRows);

  const bookedEquity = equityReconRound(accountRows.reduce((sum, row) => sum + row.closing, 0));
  const openingEquity = equityReconRound(accountRows.reduce((sum, row) => sum + row.opening, 0));
  const periodResult = equityReconRound(reports?.result?.resultat);
  // Er resultatet allerede overført til egenkapitalkontoene, står det der. Da
  // ville en ny addisjon vært en dobbelttelling, og egenkapitalen ville vokst
  // med årets resultat to ganger.
  const resultTransferred = transactions.some((row) => row.movesResult);
  const computedEquity = equityReconRound(bookedEquity + (resultTransferred ? 0 : periodResult));
  const statementEquity = equityReconRound(
    statement.reduce((sum, row) => sum + row.beregnetUt, 0) + (resultTransferred ? 0 : periodResult),
  );
  const previousEquity = equityReconPreviousPeriod({ range, resultTransferred, openingEquity });

  const liquidity = equityReconLiquidity({ state, trial, range, equity: computedEquity });
  const capital = equityReconCapital({ state, statement, decision, documents });
  const disposition = equityReconDisposition({ year: range.year, decision, transactions });

  const main = {
    bookedEquity,
    openingEquity,
    periodResult,
    resultTransferred,
    computedEquity,
    statementEquity,
    // Den ene differansen avstemmingen selv eier: oppstillingens beregnede
    // egenkapital mot den bokførte. Er den null, er egenkapitalen matematisk
    // avstemt — og det sier fortsatt ingenting om vedtakene.
    unexplained: equityReconRound(statementEquity - computedEquity),
    previousEquity,
    change: equityReconRound(computedEquity - previousEquity),
    equityRatio: liquidity.equityRatio,
    transactionCount: transactions.length,
    readAt: new Date().toISOString(),
  };

  const periodLabel = typeof window.avstPeriodLabel === 'function'
    ? window.avstPeriodLabel(periodId)
    : periodId;
  const items = equityReconItems({
    transactions, statement, accountRows, capital, main, periodLabel,
  });
  const counts = equityReconCounts(items);

  return {
    periodId,
    periodLabel,
    range,
    from: range.yearFrom,
    to: range.to,
    year: range.year,
    accountRows,
    statement,
    transactions,
    decision,
    disposition,
    capital,
    liquidity,
    items,
    counts,
    main,
    ledgerLines: lines.filter((line) => equityCodes.has(line.accountCode)),
    sources: equityReconSources({ decision, capital, disposition, liquidity }),
    checks: equityReconChecks({ counts, statement, disposition }),
  };
};

Object.assign(window, {
  buildEquityReconciliationView, equityReconTrialMap, equityReconPreviousPeriod,
});
